<template>
  <v-dialog v-model="dialog" max-width="600">
    <template #activator="{ props }">
      <slot name="activator" :props="props"></slot>
    </template>

    <v-card class="onest-font rounded-2xl shadow-lg border">
      <v-card-title
        class="d-flex justify-space-between align-center px-6 py-4 border-b"
      >
        <span class="text-h6 font-bold text-gray-900"
          >Associar Conta de Teste</span
        >
        <v-btn
          variant="plain"
          rounded="xl"
          icon="mdi-close"
          size="small"
          @click="dialog = false"
        ></v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <div class="mb-4 text-sm text-gray-600">
          Selecione o papel do usuário <strong>{{ user.nome }}</strong> para o
          qual deseja associar uma conta de teste cadastrada no roteiro de
          validação correspondente.
        </div>

        <!-- Seletor de Papel Aceito -->
        <v-select
          v-model="selectedAtorId"
          :items="papeisAceitos"
          item-title="nomeAtor"
          item-value="idAtor"
          label="Selecione o Papel"
          variant="outlined"
          density="comfortable"
          class="mb-4 rounded-xl"
          @update:model-value="fetchContasTeste"
        ></v-select>

        <div v-if="loading" class="d-flex flex-col align-center py-6">
          <v-progress-circular
            indeterminate
            color="primary"
            class="mb-2"
          ></v-progress-circular>
          <span class="text-xs text-gray-500">Buscando contas de teste...</span>
        </div>

        <div
          v-else-if="contasTeste.length === 0"
          class="py-6 text-center text-gray-500 border border-dashed rounded-xl bg-gray-50/50"
        >
          <v-icon size="40" class="text-gray-300 mb-2"
            >mdi-account-question-outline</v-icon
          >
          <p class="text-sm font-semibold">Nenhuma conta de teste encontrada</p>
          <p class="text-xs text-gray-400 mt-1">
            Nenhuma conta de teste foi cadastrada no roteiro de validação deste
            papel.
          </p>
        </div>

        <div v-else class="space-y-3 max-h-[300px] overflow-y-auto pr-1">
          <div
            v-for="conta in contasTeste"
            :key="conta.idConta"
            class="border rounded-xl p-4 bg-gray-50/50 hover:bg-gray-50 transition-colors d-flex justify-space-between align-center"
          >
            <div class="flex-grow-1 mr-4">
              <div
                class="font-bold text-gray-800 text-sm mb-1 d-flex align-center"
              >
                <v-icon size="16" class="mr-1 text-gray-600"
                  >mdi-account</v-icon
                >
                {{ conta.usuarioAcesso }}
              </div>
              <div class="text-xs text-gray-500 d-flex align-center">
                <v-icon size="16" class="mr-1 text-gray-600">mdi-key</v-icon>
                Senha:
                <strong class="ml-1 text-gray-700 font-semibold">{{
                  conta.senhaAcesso
                }}</strong>
              </div>

              <!-- Badges de status -->
              <div class="mt-2">
                <v-chip
                  v-if="
                    (user.isExterno && conta.idUsuarioExterno === user.id) ||
                    (!user.isExterno && conta.idUsuarioInterno === user.id)
                  "
                  color="success"
                  size="x-small"
                  variant="flat"
                  class="font-bold"
                >
                  Associada a este usuário
                </v-chip>
                <v-chip
                  v-else-if="conta.idUsuarioExterno || conta.idUsuarioInterno"
                  color="grey"
                  size="x-small"
                  variant="flat"
                  class="font-bold"
                >
                  Associada a: {{ conta.usuarioAssociado?.nome }}
                </v-chip>
                <v-chip
                  v-else
                  color="warning"
                  size="x-small"
                  variant="tonal"
                  class="font-bold"
                >
                  Disponível
                </v-chip>
              </div>
            </div>

            <div>
              <v-btn
                v-if="
                  (user.isExterno && conta.idUsuarioExterno === user.id) ||
                  (!user.isExterno && conta.idUsuarioInterno === user.id)
                "
                color="error"
                variant="outlined"
                size="small"
                class="rounded-xl font-bold"
                :loading="savingId === conta.idConta"
                @click="desassociarConta(conta.idConta)"
              >
                Desassociar
              </v-btn>
              <v-btn
                v-else
                color="primary"
                variant="flat"
                size="small"
                class="rounded-xl font-bold"
                :disabled="!!conta.idUsuarioExterno || !!conta.idUsuarioInterno"
                :loading="savingId === conta.idConta"
                @click="associarConta(conta.idConta)"
              >
                Associar
              </v-btn>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions
        class="px-6 py-4 border-t d-flex justify-end bg-gray-50/50 rounded-b-2xl"
      >
        <v-btn
          color="grey-darken-1"
          variant="outlined"
          class="rounded-xl px-5 font-bold"
          @click="dialog = false"
        >
          Fechar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';

const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000';

/**
 * @module ModalAssociarContaTeste
 * @description Modal para associar contas de teste de um ator/roteiro a um usuário convidado aceito.
 */
export default {
  name: 'ModalAssociarContaTeste',
  props: {
    user: {
      type: Object,
      required: true,
    },
    idValidacao: {
      type: [Number, String],
      required: true,
    },
  },
  emits: ['associacao-alterada'],
  data() {
    return {
      dialog: false,
      selectedAtorId: null,
      contasTeste: [],
      loading: false,
      savingId: null,
    };
  },
  computed: {
    papeisAceitos() {
      if (!this.user.papeis) {
        return [];
      }

      return this.user.papeis.filter((p) => p.flagAceito);
    },
  },
  watch: {
    dialog(val) {
      if (val) {
        this.dialogOpened();
      }
    },
  },
  methods: {
    dialogOpened() {
      if (this.papeisAceitos.length > 0) {
        this.selectedAtorId = this.papeisAceitos[0].idAtor;
        this.fetchContasTeste();
      } else {
        this.selectedAtorId = null;
        this.contasTeste = [];
      }
    },
    async fetchContasTeste() {
      if (!this.selectedAtorId || !this.idValidacao) {
        return;
      }
      this.loading = true;
      try {
        const response = await axios.get(
          `${apiUrl}/api/v1/usuario-externo/contas-teste`,
          {
            params: {
              idAtor: this.selectedAtorId,
              idValidacao: this.idValidacao,
            },
            headers: {
              Authorization: `Bearer ${
                this.$store?.state?.token || localStorage.getItem('token')
              }`,
            },
          },
        );
        this.contasTeste = response.data || [];
      } catch (error) {
        console.error('Erro ao buscar contas de teste:', error);
        this.$toast.error('Erro ao buscar contas de teste.');
      } finally {
        this.loading = false;
      }
    },
    async associarConta(idConta) {
      this.savingId = idConta;
      try {
        const token =
          this.$store?.state?.token || localStorage.getItem('token');
        await axios.post(
          `${apiUrl}/api/v1/usuario-externo/associar-conta`,
          {
            idConta,
            idUsuario: this.user.id,
            isExterno: this.user.isExterno,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        this.$toast.success('Conta associada com sucesso!');
        await this.fetchContasTeste();
        this.$emit('associacao-alterada');
      } catch (error) {
        console.error('Erro ao associar conta:', error);
        this.$toast.error(
          error.response?.data?.error || 'Erro ao associar conta.',
        );
      } finally {
        this.savingId = null;
      }
    },
    async desassociarConta(idConta) {
      this.savingId = idConta;
      try {
        const token =
          this.$store?.state?.token || localStorage.getItem('token');
        await axios.post(
          `${apiUrl}/api/v1/usuario-externo/associar-conta`,
          {
            idConta,
            idUsuario: null,
            isExterno: this.user.isExterno,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        this.$toast.success('Conta desassociada com sucesso!');
        await this.fetchContasTeste();
        this.$emit('associacao-alterada');
      } catch (error) {
        console.error('Erro ao desassociar conta:', error);
        this.$toast.error(
          error.response?.data?.error || 'Erro ao desassociar conta.',
        );
      } finally {
        this.savingId = null;
      }
    },
  },
};
</script>

<style scoped>
.onest-font {
  font-family: 'Onest', sans-serif !important;
}
.space-y-3 > :not([hidden]) ~ :not([hidden]) {
  margin-top: 0.75rem;
}
</style>
